import { useEffect, useState } from 'react';
import type {
  VueFormatterServiceInterfaceV1,
  LocaleServiceV1,
  Logger,
} from './feature-service-types';
import type { AppContent } from '../../@types/interfaces';
import { isWltpComplianceSatisfied } from './wltpCompliance';

export interface UseWltpComplianceGateArgs {
  content: AppContent | undefined;
  vueFormatterService: VueFormatterServiceInterfaceV1 | undefined;
  localeService: LocaleServiceV1 | undefined;
  logger?: Pick<Logger, 'warn' | 'error'>;
  isMarketComplianceEnabled?: boolean;
  initialCompliance?: boolean;
}

export const useWltpComplianceGate = ({
  content,
  vueFormatterService,
  localeService,
  logger,
  isMarketComplianceEnabled,
  initialCompliance,
}: UseWltpComplianceGateArgs): boolean => {
  const isGateActive = Boolean(isMarketComplianceEnabled);
  const isServerResultAvailable = initialCompliance !== undefined;

  const [clientCompliance, setClientCompliance] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    const isClientCheckNeeded = isGateActive && !isServerResultAvailable;
    if (!isClientCheckNeeded) return undefined;
    if (!content || !vueFormatterService || !localeService) return undefined;

    const controller = new AbortController();
    const runCheck = async () => {
      const isCompliant = await isWltpComplianceSatisfied(
        content.consumptionAndEmission,
        vueFormatterService,
        localeService,
        logger,
      );
      if (!controller.signal.aborted) {
        setClientCompliance(isCompliant);
      }
    };
    void runCheck();
    return () => {
      controller.abort();
    };
  }, [isGateActive, isServerResultAvailable, content, localeService, vueFormatterService, logger]);

  if (!isGateActive) return true;

  const isCompliant = isServerResultAvailable ? initialCompliance : clientCompliance;
  return Boolean(isCompliant);
};
